import React from 'react';
import PropTypes from 'prop-types';
import { Switch, Route, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { GrowProvider, withGrow } from 'hoc/grow';
import {
  isDev,
  unregisterNetworkStatus,
  registerNetworkStatus,
} from 'common/utils';
import * as constants from 'common/constants';
import mixpanel from 'mixpanel-browser';
import Auth from '@aws-amplify/auth';
import { field, onLogout, auth } from 'actions';
import {
  ActivityDetail,
  ActivityEdit,
  ActivityPage,
  ContractDetail,
  ContractEdit,
  ContractsPage,
  ConfirmEmail,
  Dashboard,
  FieldDetail,
  FieldEdit,
  FieldUpload,
  FieldPage,
  StorageEdit,
  StoragePage,
  StorageDetail,
  NotFound,
} from 'pages';
import { Banner, ErrorBoundary } from 'common/components';
import { Header, Footer, OfflineBanner } from 'containers';
import './index.css';

const ENV = process.env.COGNITO_BUILD_ENV || 'test';
const GrowRoute = withRouter(
  withGrow(({ component: Component, ...rest }) => (
    <Route
      {...rest}
      render={props => <Component {...props} grow={rest.grow} />}
    />
  ))
);

class App extends React.Component {
  constructor(props) {
    super(props);

    // Flag to prevent repeated calls to `currentAuthenticatedUser`
    this.state = {
      isCheckinguser: false,
      hasCheckedUser: false,
    };
  }

  async componentDidMount() {
    // Handle network status change
    registerNetworkStatus();

    // Identify with mixpanel if possible
    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      mixpanel.identify(currentUser.attributes.email);
    } catch (err) {
      // logout user when token is invalid
      this.props.logout();
    }

    // for fonts.com
    const MTIProjectId = process.env.REACT_APP_FONT_MTI_ID;
    window.MTIProjectId = MTIProjectId;

    let mtiTracking = document.createElement('link');
    mtiTracking.type = 'text/css';
    mtiTracking.rel = 'stylesheet';
    mtiTracking.href =
      ('https:' == document.location.protocol ? 'https:' : 'http:') +
      '//fast.fonts.net/t/1.css?apiType=css&projectid=' +
      MTIProjectId;
    (
      document.getElementsByTagName('head')[0] ||
      document.getElementsByTagName('body')[0]
    ).appendChild(mtiTracking);

    // TODO: name `production` is going to be the right name?
    // track only production
    if (ENV !== 'production') {
      return;
    }
    mtiTracking = document.createElement('script');
    mtiTracking.type = 'text/javascript';
    mtiTracking.async = 'true';
    mtiTracking.src = '/assets/fonts/mtiFontTrackingCode.js';
    (
      document.getElementsByTagName('head')[0] ||
      document.getElementsByTagName('body')[0]
    ).appendChild(mtiTracking);

    // Set Raven/Sentry user context
    if (!this.state.hasCheckedUser) {
      this.setState({ isCheckingUser: true }, async () => {
        const currentUser = await Auth.currentAuthenticatedUser();
        this.setState({ hasCheckedUser: true, isCheckingUser: false });

        // Add current user to sentry report
        if (!isDev() && window && window.Raven) {
          window.Raven.setUserContext({
            email: currentUser.attributes.email,
            id: currentUser.username,
          });
        }
      });
    }
  }

  componentWillUnmount() {
    unregisterNetworkStatus();
  }

  render() {
    const { field, location, onLogout, toggleFieldView } = this.props;

    return (
      <ErrorBoundary>
        <GrowProvider>
          <div className="agi-grow-wrapper">
            <Header
              onLogout={onLogout}
              fieldState={field}
              toggleFieldView={toggleFieldView}
              location={location}
            />
            <Banner id={constants.BANNER_IDS.APP} theme={['strong', 'info']} />
            <OfflineBanner />
            <Switch>
              <Route
                exact
                path={constants.ROUTE_DASHBOARD}
                component={Dashboard}
              />
              <Route
                exact
                path={constants.ROUTE_CONFIRM_EMAIL}
                component={ConfirmEmail}
              />
              <GrowRoute
                exact
                path={constants.ROUTE_FIELDS}
                component={FieldPage}
              />
              <Route
                exact
                path={constants.ROUTE_FIELDS_ADD}
                component={FieldEdit}
              />
              <Route
                exact
                path={constants.ROUTE_FIELDS_UPLOAD}
                component={FieldUpload}
              />
              <Route
                exact
                path={constants.ROUTE_FIELDS_EDIT + '/:encodedId'}
                component={FieldEdit}
              />
              <Route
                exact
                path={constants.ROUTE_FIELDS_DETAIL + '/:encodedFieldName/:id?'}
                component={FieldDetail}
              />
              <GrowRoute
                exact
                path={constants.ROUTE_ACTIVITY}
                component={ActivityPage}
              />
              <Route
                exact
                path={constants.ROUTE_ACTIVITY_DETAIL + '/:encodedId'}
                component={ActivityDetail}
              />
              <Route
                exact
                path={constants.ROUTE_ACTIVITY + '/:id?/:type?/:extra?'}
                component={ActivityEdit}
              />
              <GrowRoute
                exact
                path={constants.ROUTE_STORAGE}
                component={StoragePage}
              />
              <Route
                exact
                path={constants.ROUTE_STORAGE_ADD}
                component={StorageEdit}
              />
              <Route
                exact
                path={constants.ROUTE_STORAGE_EDIT + '/:encodedId'}
                component={StorageEdit}
              />
              <Route
                exact
                path={constants.ROUTE_STORAGE_DETAIL + '/:encodedId'}
                component={StorageDetail}
              />
              <GrowRoute
                exact
                path={constants.ROUTE_CONTRACTS}
                component={ContractsPage}
              />
              <Route
                exact
                path={constants.ROUTE_CONTRACTS_ADD}
                component={ContractEdit}
              />
              <Route
                exact
                path={constants.ROUTE_CONTRACTS_EDIT + '/:encodedId'}
                component={ContractEdit}
              />
              <Route
                exact
                path={constants.ROUTE_CONTRACTS_DETAIL + '/:encodedId'}
                component={ContractDetail}
              />
              <Route path="/error" component={NotFound} />
              <Route component={NotFound} />
            </Switch>
            <Footer />
          </div>
        </GrowProvider>
      </ErrorBoundary>
    );
  }
}

App.propTypes = {
  auth: PropTypes.object.isRequired,
  features: PropTypes.object,
  field: PropTypes.object.isRequired,
  location: PropTypes.object, // This must be provided to <Header /> to unblock router updates
  onLogout: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  toggleFieldView: PropTypes.func.isRequired,
};

// Enable Togglable Features in the appliation
const mapState = ({ auth, field }) => ({
  auth,
  field,
});

const mapDispatch = {
  onLogout: onLogout,
  logout: auth.logout,
  toggleFieldView: field.toggleFieldView,
};

const enhance = connect(
  mapState,
  mapDispatch
);

export default withRouter(enhance(App));



// WEBPACK FOOTER //
// ./src/pages/App/BaseApp.js